import React from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import { FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { routeMap } from '@core/constants/routes'

type ErrorFallbackProps = FallbackProps & {
  title?: string
}

const is404Error = (error: Error): boolean => {
  let { message } = error

  if ('message' in error) {
    message = error.message as string
  }

  // Check for common 404 error patterns
  return (
    message?.includes('404') ||
    message?.toLowerCase().includes('not found') ||
    error.stack?.includes('404') ||
    // Check for fetch response errors
    (error as any)?.status === 404 ||
    (error as any)?.response?.status === 404
  )
}

const isNetworkError = (error: Error): boolean => {
  let { message } = error

  if ('message' in error) {
    message = error.message as string
  }

  return message?.includes('NetworkError') || message?.toLowerCase().includes('network')
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  title = 'Something went wrong',
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  const handleRetry = () => {
    resetErrorBoundary()
  }

  const handleGoHome = () => {
    navigate(routeMap.AUTHOR_V2_LANDING_PAGE, {
      replace: true,
    })
  }

  if (is404Error(error)) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('error.content_not_found')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('error.content_not_found_message')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={handleRetry} size="small">
              {t('actions.try_again')}
            </Button>
            <Button variant="contained" onClick={handleGoHome} size="small">
              {t('actions.go_to_home')}
            </Button>
          </Box>
        </Alert>
      </Box>
    )
  }

  if (isNetworkError(error)) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('error.connection_problem')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('error.connection_problem_message')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={handleRetry} size="small">
              {t('actions.try_again')}
            </Button>
            <Button variant="contained" onClick={handleGoHome} size="small">
              {t('actions.go_to_home')}
            </Button>
          </Box>
        </Alert>
      </Box>
    )
  }

  // Generic error fallback
  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('error.unexpected_error_message')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="outlined" onClick={handleRetry} size="small">
            {t('actions.try_again')}
          </Button>
          <Button variant="contained" onClick={handleGoHome} size="small">
            {t('actions.go_to_home')}
          </Button>
        </Box>
      </Alert>
    </Box>
  )
}

export default ErrorFallback
